import React, { useState } from "react";
import { AccordionIcon, Text } from "@chakra-ui/react";
import { FiShield } from "react-icons/fi";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import { SingleAccordion } from "newui/components/SingleAccordion/SingleAccordion";
import { exportPdf } from "newui/features/Settings/PdfSettings/api/design-schema-api";
import { downloadFileNoPopup } from "helpers/linkHelper";
import type { ShortlistReportContext } from "../types";
import { useHasCustomiseShortlistReport } from "hooks/featureFlags";

interface ShortlistDownloadModalProps {
	context: ShortlistReportContext;
	currentCompanyId: number;
	isOpen: boolean;
	onClose: () => void;
	onCustomise: () => void;
}

const ShortlistDownloadModal: React.FC<ShortlistDownloadModalProps> = ({
	isOpen,
	onClose,
	context,
	currentCompanyId,
	onCustomise,
}) => {
	const [isExporting, setIsExporting] = useState(false);
	const canCustomise = useHasCustomiseShortlistReport();

	const handleStandardReport = async () => {
		setIsExporting(true);
		try {
			const result = await exportPdf(
				currentCompanyId,
				context.companyIds,
				undefined,
				context.searchSectorIds?.length ? context.searchSectorIds : undefined,
				context.groupId
			);
			if (result?.download_url) {
				downloadFileNoPopup(
					result.download_url,
					result.filename || "shortlist-report.pdf",
					false
				);
			}
			onClose();
		} catch (error) {
			console.error("Failed to export shortlist PDF:", error);
		} finally {
			setIsExporting(false);
		}
	};

	const handleCustomise = () => {
		onClose();
		onCustomise();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Download Shortlist Report"
			iconType="download"
			size="md"
			closeOnEsc
		>
			<div className="flex flex-col gap-3">
				<Button
					btntype="primary"
					size="lg"
					onClick={handleStandardReport}
					isLoading={isExporting}
					loadingText="Generating..."
					className="w-full"
					disabled={isExporting}
				>
					Generat{isExporting ? "ing" : "e"} {canCustomise && "Standard "}Report
				</Button>
				{canCustomise && (
					<Button
						btntype="secondary"
						size="lg"
						onClick={handleCustomise}
						disabled={isExporting}
						className="w-full"
					>
						Customise Report
					</Button>
				)}
			</div>

			<SingleAccordion
				initialOpen={false}
				renderButtonContent={(isOpen) => (
					<div className="flex w-full items-center gap-2">
						<FiShield className="shrink-0 text-gray-500" size={16} />
						<Text
							flex="1"
							textAlign="left"
							fontSize="sm"
							fontWeight="medium"
							color="gray.600"
						>
							View Terms of Use
						</Text>
						<AccordionIcon paddingLeft={1} />
					</div>
				)}
				buttonProps={{
					className: "rounded-lg border border-blue-200 bg-blue-50 px-4 py-3",
					_hover: { bg: "blue.50" },
				}}
				panelProps={{
					className: "rounded-b-lg border border-t-0 border-blue-200 bg-blue-50 px-4 pb-4 pt-0",
				}}
			>
				<Text fontSize="sm" color="gray.500" lineHeight="tall" padding={2}>
					While we do our best to provide a platform that captures and presents
					supplier information clearly, each supplier remains responsible for the
					accuracy, currency, and completeness of the information they submit.
					Users should make their own enquiries and satisfy themselves that a
					supplier can deliver the goods or services they represent they are
					capable of providing.
				</Text>
				<Text fontSize="sm" color="gray.500" lineHeight="tall" padding={2}>
					Please refer to our{" "}
					<a
						href="https://procuracon.com.au/terms-conditions"
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue-500 underline hover:text-blue-700"
					>
						Terms and Conditions of Use
					</a>{" "}
					for further information.
				</Text>
			</SingleAccordion>
		</Modal>
	);
};

export { ShortlistDownloadModal };
